import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';

import type { ScatterBubbleCapabilities, ScatterBubbleVariant } from '../../../chart-components/unified-scatter-bubble-chart/unified-scatter-bubble-chart.types';
import { DvmBuilderStepBase, TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile } from '../../dvm-layout-builder/dvm-layout-builder.types';
import type { FormatControl, FormatControlGroup, LabelValueOption, ScatterBubbleModeOption, ScatterBubbleVariantOption } from '../../shared/self-service-config.types';
import { extractLineAreaFlatRows } from '../../shared/self-service-recompute.utils';
import { SelfServiceUiService } from '../../shared/self-service-ui.service';
import { applyScatterBubbleFormatToCapabilities } from './scatter-bubble-capabilities.utils';

/**
 * Scatter & Bubble build form (SCATTER_BUBBLE): mode toggle (scatter /
 * bubble) + variant, X / Y / Size / Colour-by / Label / Series pickers,
 * per-category colours, and its OWN config-driven Format accordion
 * (`sbFormat`, separate from the shell's `barFormat`). Renders client-side
 * from the cached dataset rows, so the tile is previewed live while the
 * form is edited; Generate also POSTs for server-side rows.
 */
@Component({
  selector: 'app-scatter-bubble-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, NgSelectModule],
  templateUrl: './scatter-bubble-step.component.html',
  styles: [':host { display: contents; }'],
})
export class ScatterBubbleStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['SCATTER_BUBBLE'] as const;
  private readonly http = inject(HttpClient);
  private readonly ui = inject(SelfServiceUiService);

  sbMode = 'scatter';
  sbAdvanced = '';
  sbAdvancedAccordionOpen = false;
  sbVariant: ScatterBubbleVariant = 'scatter';
  sbXField = '';
  sbYField = '';
  sbSizeField = '';
  sbXAggregation = 'NONE';
  sbYAggregation = 'NONE';
  sbSizeAggregation = 'NONE';
  sbColorField = '';
  sbLabelField = '';
  sbCategoryColors: Record<string, string> = {};
  sbSeries = '';
  sbAggregation = 'none';
  sbTooltipColumns: string[] = [];
  sbXLabel = '';
  sbYLabel = '';
  sbXAxisFontWeight = '';
  sbYAxisFontWeight = '';
  sbFormat: Record<string, unknown> = {};
  openSbFormatGroups = new Set<string>();
  sbDataAccordionOpen = true;

  private readonly scatterBubbleVariantsFallback: ScatterBubbleVariantOption[] = [
    { label: 'Scatter', value: 'scatter', mode: 'scatter', group: 'Scatter' },
    { label: 'Multi Scatter', value: 'multi-scatter', mode: 'scatter', group: 'Scatter', usesSeries: true, multiSeries: true },
    { label: 'Bubble', value: 'bubble', mode: 'bubble', group: 'Bubble', usesSize: true, bubble: true },
    { label: 'Quadrant', value: 'quadrant', mode: 'scatter', group: 'Advanced', quadrant: true },
  ];
  get scatterBubbleVariants(): ScatterBubbleVariantOption[] {
    return this.host.layout.options?.scatterBubbleVariants?.length
      ? (this.host.layout.options.scatterBubbleVariants as ScatterBubbleVariantOption[])
      : this.scatterBubbleVariantsFallback;
  }
  private readonly sbModesFallback: ScatterBubbleModeOption[] = [
    { label: 'Scatter', value: 'scatter', icon: 'scatter_plot' },
    { label: 'Bubble', value: 'bubble', icon: 'bubble_chart' },
  ];
  get sbModes(): ScatterBubbleModeOption[] {
    return this.host.layout.options?.scatterBubbleModes?.length
      ? (this.host.layout.options.scatterBubbleModes as ScatterBubbleModeOption[])
      : this.sbModesFallback;
  }
  private get sbDefaultMode(): string {
    return this.sbModes[0]?.value ?? 'scatter';
  }
  private sbVariantModeOf(v: ScatterBubbleVariantOption): string {
    return v.mode ?? (v.bubble ? 'bubble' : this.sbDefaultMode);
  }
  private sbVariantIsAdvanced(v: ScatterBubbleVariantOption): boolean {
    return v.group === 'Advanced';
  }
  private sbVariantItemsCache: ScatterBubbleVariantOption[] = [];
  private sbVariantItemsCacheKey: { source: ScatterBubbleVariantOption[]; mode: string } | null = null;
  get sbVariantItems(): ScatterBubbleVariantOption[] {
    const source = this.scatterBubbleVariants;
    if (this.sbVariantItemsCacheKey?.source !== source || this.sbVariantItemsCacheKey.mode !== this.sbMode) {
      this.sbVariantItemsCache = source.filter((v) => this.sbVariantModeOf(v) === this.sbMode && !this.sbVariantIsAdvanced(v));
      this.sbVariantItemsCacheKey = { source, mode: this.sbMode };
    }
    return this.sbVariantItemsCache;
  }
  private readonly sbAdvancedOptionsFallback: ScatterBubbleVariantOption[] = [
    { label: 'Simple', value: '', hint: 'The plain base chart — no analytic overlay applied.' },
    { label: 'Quadrant', value: 'quadrant', quadrant: true },
    { label: 'Regression', value: 'regression', regression: true },
    { label: 'Correlation', value: 'correlation', correlation: true },
    { label: 'Cluster Scatter', value: 'cluster', cluster: true },
  ];
  private sbAdvancedItemsCache: ScatterBubbleVariantOption[] = [];
  private sbAdvancedItemsCacheKey: { source: ScatterBubbleVariantOption[]; mode: string } | null = null;
  get sbAdvancedVariantItems(): ScatterBubbleVariantOption[] {
    const source = this.host.layout.options?.scatterBubbleAdvancedOptions?.length
      ? (this.host.layout.options.scatterBubbleAdvancedOptions as ScatterBubbleVariantOption[])
      : this.sbAdvancedOptionsFallback;
    if (this.sbAdvancedItemsCacheKey?.source !== source || this.sbAdvancedItemsCacheKey.mode !== this.sbMode) {
      this.sbAdvancedItemsCache = source.filter((v) => !v.mode || v.mode === this.sbMode);
      this.sbAdvancedItemsCacheKey = { source, mode: this.sbMode };
    }
    return this.sbAdvancedItemsCache;
  }
  private readonly sbAggregationOptionsFallback: LabelValueOption[] = [
    { label: 'None (raw points)', value: 'none' },
    { label: 'Sum', value: 'sum' },
    { label: 'Average', value: 'avg' },
    { label: 'Count', value: 'count' },
    { label: 'Min', value: 'min' },
    { label: 'Max', value: 'max' },
  ];
  get sbAggregationOptions(): LabelValueOption[] {
    return this.host.layout.options?.sbAggregations?.length
      ? this.host.layout.options.sbAggregations
      : this.sbAggregationOptionsFallback;
  }
  private get sbAdvancedEntry(): ScatterBubbleVariantOption | undefined {
    if (!this.sbAdvanced) return undefined;
    return this.sbAdvancedVariantItems.find((v) => v.value === this.sbAdvanced);
  }
  setSbMode(mode: string): void {
    if (this.sbMode === mode) return;
    this.sbMode = mode;
    if (!this.sbVariantItems.some((v) => v.value === this.sbVariant)) {
      this.sbVariant = (this.sbVariantItems[0]?.value ?? 'scatter') as ScatterBubbleVariant;
    }
    if (this.sbAdvanced && !this.sbAdvancedVariantItems.some((v) => v.value === this.sbAdvanced)) {
      this.sbAdvanced = '';
    }
    this.onScatterBubbleChange();
  }
  toggleSbAdvanced(value: string): void {
    this.sbAdvanced = this.sbAdvanced === value ? '' : value;
    this.onScatterBubbleChange();
  }
  private sbModeForVariant(variant: string): string {
    const entry = this.scatterBubbleVariants.find((v) => v.value === variant);
    return entry ? this.sbVariantModeOf(entry) : this.sbDefaultMode;
  }
  private get sbVariantEntry(): ScatterBubbleVariantOption | undefined {
    return this.scatterBubbleVariants.find((v) => v.value === this.sbVariant);
  }
  get sbVariantUsesSeries(): boolean {
    return this.sbVariantEntry?.usesSeries === true || this.sbAdvancedEntry?.usesSeries === true;
  }
  get sbVariantUsesSize(): boolean {
    return this.sbVariantEntry?.usesSize === true || this.sbAdvancedEntry?.usesSize === true;
  }
  get sbVariantHint(): string {
    return this.sbVariantEntry?.hint || '';
  }
  get sbAdvancedHint(): string {
    return this.sbAdvancedEntry?.hint || '';
  }

  private sbFormatGroupsCache: FormatControlGroup[] | null = null;
  private sbFormatGroupsCacheType = '';
  get scatterBubbleFormatGroups(): FormatControlGroup[] {
    if (this.sbFormatGroupsCache && this.sbFormatGroupsCacheType === this.host.selectedLayoutType) {
      return this.sbFormatGroupsCache;
    }
    const applicable = this.host.applicableFormatControls(this.host.selectedLayoutType);
    const byGroup = new Map<string, FormatControl[]>();
    const seen: string[] = [];
    applicable.forEach((control) => {
      const group = control.group || 'Format';
      if (!byGroup.has(group)) { byGroup.set(group, []); seen.push(group); }
      byGroup.get(group)!.push(control);
    });
    const order = this.host.formatGroupOrder;
    seen.sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia === -1 ? order.length + seen.indexOf(a) : ia) - (ib === -1 ? order.length + seen.indexOf(b) : ib);
    });
    const exposed = seen.filter((name) => this.host.chartExposesSection(name));
    this.sbFormatGroupsCacheType = this.host.selectedLayoutType;
    this.sbFormatGroupsCache = exposed.map((name) => ({ name, controls: byGroup.get(name)! }));
    return this.sbFormatGroupsCache;
  }
  isSbFormatGroupOpen(group: string): boolean {
    return this.openSbFormatGroups.has(group);
  }
  toggleSbFormatGroup(group: string): void {
    const next = new Set(this.openSbFormatGroups);
    if (next.has(group)) next.delete(group);
    else next.add(group);
    this.openSbFormatGroups = next;
  }
  private seedScatterBubbleFormatDefaults(): void {
    this.host.applicableFormatControls('SCATTER_BUBBLE').forEach((control) => {
      if (control.default !== undefined && this.sbFormat[control.key] === undefined) {
        this.sbFormat[control.key] = control.default;
      }
    });
  }

  private sbColumnsCache: string[] = [];
  private sbColumnsKey = '';
  get scatterBubbleColumns(): string[] {
    const base = this.host.drillDatasetColumns;
    const key = base.join('|');
    if (key !== this.sbColumnsKey) {
      this.sbColumnsKey = key;
      this.sbColumnsCache = [...base];
    }
    return this.sbColumnsCache;
  }

  private static readonly SB_MAX_CATEGORY_COLORS = 40;
  private sbCatRows: Array<Record<string, unknown>> | undefined;
  private sbCatField = '';
  private sbCatCache: string[] = [];
  get sbColorCategories(): string[] {
    const rows = this.host.cachedRows(this.host.drillDataset);
    const field = this.sbColorField;
    if (rows === this.sbCatRows && field === this.sbCatField) return this.sbCatCache;
    this.sbCatRows = rows;
    this.sbCatField = field;
    const out: string[] = [];
    if (field && rows) {
      const seen = new Set<string>();
      for (const row of rows) {
        const value = String(row[field] ?? '');
        if (seen.has(value)) continue;
        seen.add(value);
        out.push(value);
        if (out.length >= ScatterBubbleStepComponent.SB_MAX_CATEGORY_COLORS) break;
      }
    }
    this.sbCatCache = out;
    return out;
  }
  sbCategoryColor(category: string, index: number): string {
    const explicit = this.sbCategoryColors[category];
    if (explicit) return explicit;
    const defaults = this.host.layout.options?.scatterBubbleChartDefaults as { palette?: string[] } | undefined;
    const palette = this.host.resolvePaletteColors(this.sbFormat['palette'] ? String(this.sbFormat['palette']) : undefined)
      || defaults?.palette
      || [];
    return palette.length ? palette[index % palette.length] : '#1f6feb';
  }
  get hasSbCategoryColors(): boolean {
    return Object.keys(this.sbCategoryColors).length > 0;
  }
  onSbCategoryColorChange(category: string, color: string): void {
    const next = { ...this.sbCategoryColors };
    if (color) next[category] = color;
    else delete next[category];
    this.sbCategoryColors = next;
    this.onScatterBubbleChange();
  }
  resetSbCategoryColors(): void {
    this.sbCategoryColors = {};
    this.onScatterBubbleChange();
  }

  onSbXFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.sbXField = mapping.field;
    if (!this.sbXLabel) this.sbXLabel = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbYFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.sbYField = mapping.field;
    if (!this.sbYLabel) this.sbYLabel = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbSizeFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.sbSizeField = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbColorFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.sbColorField = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbLabelFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.sbLabelField = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbSeriesFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.sbSeries = mapping.field;
    this.onScatterBubbleChange();
  }

  onScatterBubbleChange(): void {
    this.host.invalidatePreview();
    this.refreshEditingScatterBubbleTile();
  }
  onScatterBubbleFormatChange(): void {
    if (typeof this.sbFormat['title'] === 'string') this.host.title = this.sbFormat['title'] as string;
    this.refreshEditingScatterBubbleTile();
  }
  private refreshEditingScatterBubbleTile(): void {
    if (!this.active) return;
    if (this.host.editingTileId) {
      const index = this.host.dashboardTiles.findIndex((t) => t.id === this.host.editingTileId);
      const tile = index >= 0 ? this.host.dashboardTiles[index] : null;
      if (tile && tile.layoutType === 'SCATTER_BUBBLE') {
        this.host.dashboardTiles = this.host.dashboardTiles.map((t, i) => (i === index ? this.rebuildScatterBubbleTile(tile) : t));
      }
      return;
    }
    if (!this.canPreviewScatterBubble) {
      this.host.removePreviewTile();
      return;
    }
    const existingIndex = this.host.previewTileId ? this.host.dashboardTiles.findIndex((t) => t.id === this.host.previewTileId) : -1;
    if (existingIndex < 0) {
      const base = this.host.newTileBase(this.host.buildPayload());
      this.host.previewTileId = base.id;
      const draft = this.rebuildScatterBubbleTile({ ...base, layoutType: 'SCATTER_BUBBLE' });
      this.host.dashboardTiles = [...this.host.dashboardTiles, draft];
    } else {
      const tile = this.host.dashboardTiles[existingIndex];
      this.host.dashboardTiles = this.host.dashboardTiles.map((t, i) => (i === existingIndex ? this.rebuildScatterBubbleTile(tile) : t));
    }
  }
  private rebuildScatterBubbleTile(tile: DashboardTile): DashboardTile {
    const format = { ...this.host.defaultTileFormat('SCATTER_BUBBLE'), ...this.sbFormat, title: this.host.title.trim() || undefined };
    const rows = this.host.cachedRows(this.host.drillDataset) || [];
    return {
      ...tile,
      title: this.host.title.trim() || tile.title || 'Untitled',
      format,
      scatterBubbleRows: rows,
      scatterBubbleData: this.buildScatterBubbleWidgetData(tile.id, format),
    };
  }

  get canPreviewScatterBubble(): boolean {
    if (!this.active || !this.host.drillDataset) return false;
    if (!this.host.cachedRows(this.host.drillDataset)?.length) return false;
    if (!this.sbXField || !this.sbYField) return false;
    if (this.sbVariantUsesSize && !this.sbSizeField) return false;
    if (this.sbVariantUsesSeries && !this.sbSeries) return false;
    return true;
  }

  private buildScatterBubbleColumns(capabilities: ScatterBubbleCapabilities): Array<Record<string, unknown>> {
    const dataset = this.host.drillDataset;
    const seen = new Set<string>();
    const columns: Array<Record<string, unknown>> = [];
    const measureFields: Array<[string | undefined, string | undefined]> = [
      [capabilities.xField, capabilities.xFieldAggregation],
      [capabilities.yField, capabilities.yFieldAggregation],
      [capabilities.sizeField, capabilities.sizeFieldAggregation],
    ];
    for (const [raw, aggregation] of measureFields) {
      const field = String(raw || '').trim();
      if (!field || seen.has(field)) continue;
      seen.add(field);
      const agg = aggregation && aggregation !== 'NONE' ? aggregation : undefined;
      columns.push({ dataset, field, displayName: field, ...(agg ? { aggregation: agg } : {}) });
    }
    const dimensionFields = [capabilities.colorField, capabilities.labelField, capabilities.series, ...(capabilities.tooltipColumns || [])];
    for (const raw of dimensionFields) {
      const field = String(raw || '').trim();
      if (!field || seen.has(field)) continue;
      seen.add(field);
      columns.push({ dataset, field, displayName: field });
    }
    return columns;
  }

  private buildScatterBubbleTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.host.newTileBase(payload);
    base.format = { ...this.host.defaultTileFormat('SCATTER_BUBBLE'), ...this.sbFormat, title: this.host.title.trim() || undefined };
    const rows = this.host.cachedRows(this.host.drillDataset) || [];
    return { ...base, scatterBubbleRows: rows, scatterBubbleData: this.buildScatterBubbleWidgetData(base.id, base.format) };
  }

  private applyScatterBubbleComputeResponse(payload: Record<string, unknown>, response: Record<string, unknown> | null): void {
    const rows = extractLineAreaFlatRows(response?.['responseList']) ?? extractLineAreaFlatRows(response);
    if (!rows?.length) return;
    const componentId = String(payload['componentId'] ?? '');
    if (!componentId) return;
    const apply = (t: DashboardTile): DashboardTile => {
      if (t.componentId !== componentId || !t.scatterBubbleData) return t;
      if (!this.host.rowsFitClientChart(rows, t.scatterBubbleData, 'xField')) return t;
      return { ...t, scatterBubbleRows: rows, scatterBubbleData: { ...t.scatterBubbleData, staticData: rows } };
    };
    this.host.dashboardTiles = this.host.dashboardTiles.map((t) => {
      const updated = apply(t);
      if (updated !== t) return updated;
      if (!t.groupMembers?.length) return t;
      const members = t.groupMembers.map(apply);
      return members.some((m, i) => m !== t.groupMembers![i]) ? { ...t, groupMembers: members } : t;
    });
  }

  private static readonly SB_NON_FLAG_KEYS = new Set(['label', 'value', 'mode', 'group', 'hint', 'icon', 'usesSize', 'usesSeries']);

  private buildScatterBubbleCapabilities(): ScatterBubbleCapabilities {
    const base = this.sbVariantEntry;
    const adv = this.sbAdvancedEntry;
    const variantFlags: Record<string, boolean> = {};
    [base, adv].forEach((entry) => {
      if (!entry) return;
      Object.entries(entry as Record<string, unknown>).forEach(([key, val]) => {
        if (val === true && !ScatterBubbleStepComponent.SB_NON_FLAG_KEYS.has(key)) variantFlags[key] = true;
      });
    });
    return {
      variant: this.sbVariant,
      advanced: this.sbAdvanced || undefined,
      xField: this.sbXField,
      yField: this.sbYField,
      xFieldAggregation: this.sbXAggregation || 'NONE',
      yFieldAggregation: this.sbYAggregation || 'NONE',
      sizeField: this.sbVariantUsesSize && this.sbSizeField ? this.sbSizeField : undefined,
      ...(this.sbVariantUsesSize && this.sbSizeField ? { sizeFieldAggregation: this.sbSizeAggregation || 'NONE' } : {}),
      colorField: this.sbColorField || undefined,
      labelField: this.sbLabelField || undefined,
      ...(this.sbColorField && this.hasSbCategoryColors ? { categoryColors: { ...this.sbCategoryColors } } : {}),
      series: this.sbVariantUsesSeries && this.sbSeries ? this.sbSeries : undefined,
      aggregation: (this.sbAggregation || 'none') as ScatterBubbleCapabilities['aggregation'],
      tooltipColumns: this.sbTooltipColumns.length ? [...this.sbTooltipColumns] : undefined,
      xLabel: this.sbXLabel || this.sbXField,
      yLabel: this.sbYLabel || this.sbYField,
      ...(this.sbXAxisFontWeight ? { xAxisFontWeight: this.sbXAxisFontWeight } : {}),
      ...(this.sbYAxisFontWeight ? { yAxisFontWeight: this.sbYAxisFontWeight } : {}),
      ...(this.host.layout.options?.scatterBubbleChartDefaults
        ? { chartDefaults: this.host.layout.options.scatterBubbleChartDefaults as ScatterBubbleCapabilities['chartDefaults'] }
        : {}),
      variantFlags,
    } as ScatterBubbleCapabilities;
  }

  private buildScatterBubbleWidgetData(tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    const rows = this.host.cachedRows(this.host.drillDataset) || [];
    const capabilities = applyScatterBubbleFormatToCapabilities(this.buildScatterBubbleCapabilities(), format, (key) => this.host.resolvePaletteColors(key));
    return {
      widgetId: 'dvm-tile-sb-' + tileId,
      title: this.host.title.trim(),
      capabilities,
      staticData: rows,
      height: '100%',
    };
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    if (!this.host.selectedDatasets.length) return ['Dataset'];
    const missing: string[] = [];
    if (!this.sbXField) missing.push('X axis column');
    if (!this.sbYField) missing.push('Y axis column');
    if (this.sbVariantUsesSize && !this.sbSizeField) missing.push('Size column');
    if (this.sbVariantUsesSeries && !this.sbSeries) missing.push('Series column');
    return missing;
  }

  buildPayload(): Record<string, unknown> {
    const capabilities = this.buildScatterBubbleCapabilities();
    return {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: 'CHART',
      chartType: this.host.selectedLayoutType, // 'SCATTER_BUBBLE'
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      columns: this.buildScatterBubbleColumns(capabilities),
      capabilities: capabilities as unknown as Record<string, unknown>,
      ...this.host.headerFormatPayload(),
    };
  }

  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void {
    const caps = (payload['capabilities'] as Record<string, unknown> | undefined)
      ?? ((tile.scatterBubbleData?.['capabilities'] as Record<string, unknown> | undefined))
      ?? {};
    const savedVariant = String(caps['variant'] ?? 'scatter');
    const isBaseVariant = this.scatterBubbleVariants.some(
      (v) => v.value === savedVariant && !this.sbVariantIsAdvanced(v),
    );
    if (isBaseVariant) {
      this.sbVariant = savedVariant as ScatterBubbleVariant;
      this.sbAdvanced = String(caps['advanced'] ?? '');
    } else {
      this.sbVariant = 'scatter';
      this.sbAdvanced = savedVariant;
    }
    this.sbMode = this.sbModeForVariant(this.sbVariant);
    this.sbAdvancedAccordionOpen = !!this.sbAdvanced;
    this.sbXField = String(caps['xField'] ?? '');
    this.sbYField = String(caps['yField'] ?? '');
    this.sbSizeField = String(caps['sizeField'] ?? '');
    this.sbXAggregation = String(caps['xFieldAggregation'] ?? 'NONE');
    this.sbYAggregation = String(caps['yFieldAggregation'] ?? 'NONE');
    this.sbSizeAggregation = String(caps['sizeFieldAggregation'] ?? 'NONE');
    this.sbColorField = String(caps['colorField'] ?? '');
    this.sbLabelField = String(caps['labelField'] ?? '');
    const savedCategoryColors = caps['categoryColors'];
    this.sbCategoryColors = savedCategoryColors && typeof savedCategoryColors === 'object'
      ? Object.fromEntries(
          Object.entries(savedCategoryColors as Record<string, unknown>)
            .filter(([, v]) => typeof v === 'string' && !!v)
            .map(([k, v]) => [k, String(v)]),
        )
      : {};
    this.sbSeries = String(caps['series'] ?? '');
    this.sbAggregation = String(caps['aggregation'] ?? 'none');
    this.sbTooltipColumns = Array.isArray(caps['tooltipColumns']) ? (caps['tooltipColumns'] as string[]).map(String) : [];
    this.sbXLabel = String(caps['xLabel'] ?? '');
    this.sbYLabel = String(caps['yLabel'] ?? '');
    this.sbXAxisFontWeight = String(caps['xAxisFontWeight'] ?? '');
    this.sbYAxisFontWeight = String(caps['yAxisFontWeight'] ?? '');
    this.sbFormat = { ...(tile.format || {}) };
    if (this.host.drillDataset) this.host.loadColumnsForDataset(this.host.drillDataset);
  }

  reset(): void {
    this.sbMode = this.sbDefaultMode;
    this.sbAdvanced = '';
    this.sbAdvancedAccordionOpen = false;
    this.sbVariant = (this.sbVariantItems[0]?.value ?? 'scatter') as ScatterBubbleVariant;
    this.sbXField = '';
    this.sbYField = '';
    this.sbSizeField = '';
    this.sbXAggregation = 'NONE';
    this.sbYAggregation = 'NONE';
    this.sbSizeAggregation = 'NONE';
    this.sbColorField = '';
    this.sbLabelField = '';
    this.sbCategoryColors = {};
    this.sbSeries = '';
    this.sbAggregation = 'none';
    this.sbTooltipColumns = [];
    this.sbXLabel = '';
    this.sbYLabel = '';
    this.sbXAxisFontWeight = '';
    this.sbYAxisFontWeight = '';
    this.sbFormat = {};
  }

  /** Seed the scatter Format accordion defaults whenever this family becomes active. */
  onLayoutActivated(): void {
    this.seedScatterBubbleFormatDefaults();
  }

  onDatasetRowsLoaded(): void {
    this.refreshEditingScatterBubbleTile();
  }

  /** Promotes the live preview tile (or adds a new one), then POSTs for server-computed rows. */
  generate(payload: Record<string, unknown>, url: string): boolean {
    this.host.isGenerating = false;
    if (this.host.previewTileId && this.host.dashboardTiles.some((t) => t.id === this.host.previewTileId)) {
      const id = this.host.previewTileId;
      this.host.dashboardTiles = this.host.dashboardTiles.map((t) =>
        t.id === id ? { ...this.rebuildScatterBubbleTile(t), componentId: this.host.componentId, payload } : t,
      );
      this.host.previewTileId = null;
    } else {
      this.host.addTile(this.buildScatterBubbleTile(payload));
    }
    this.http.post<Record<string, unknown>>(url, payload).subscribe({
      next: (response) => {
        this.applyScatterBubbleComputeResponse(payload, response);
        this.ui.showSuccess('Scatter / bubble chart added to the dashboard.');
      },
      error: () => this.ui.showError('Scatter / bubble chart added, but saving its configuration failed.'),
    });
    return true;
  }

  // ── Tile-side hooks (DvmBuilderStep) ──────────────────────────────────────

  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    if (!tile.scatterBubbleData) return;
    const capabilities = applyScatterBubbleFormatToCapabilities(
      { ...(tile.scatterBubbleData['capabilities'] as unknown as ScatterBubbleCapabilities) },
      format,
      (key) => this.host.resolvePaletteColors(key),
    );
    tile.scatterBubbleData = {
      ...tile.scatterBubbleData,
      title: (format['title'] as string) || tile.title,
      capabilities: capabilities as unknown as Record<string, unknown>,
    };
  }

  applyRecomputeResult(tile: DashboardTile, { result }: TileRecomputeResult): boolean {
    const rows = (Array.isArray(result['data'])
      ? result['data']
      : ((result['data'] as { data?: unknown })?.data ?? [])) as Array<Record<string, unknown>>;
    if (tile.scatterBubbleData && this.host.rowsFitClientChart(rows, tile.scatterBubbleData, 'xField')) {
      tile.scatterBubbleData = { ...tile.scatterBubbleData, staticData: Array.isArray(rows) ? rows : [] };
      return true;
    }
    return false;
  }
}
